import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { ROLE_LABELS, dashboardVariantsForRoles, defaultDashboardVariant, type DashboardVariant } from "@/lib/roles";
import { useBranch } from "@/context/BranchContext";
import { ManagementDashboard } from "@/features/dashboards/ManagementDashboard";
import { BiomedDashboard } from "@/features/dashboards/BiomedDashboard";
import { FinanceDashboardView } from "@/features/dashboards/FinanceDashboardView";
import { DepartmentDashboard } from "@/features/dashboards/DepartmentDashboard";
import { PurchaseDashboard } from "@/features/dashboards/PurchaseDashboard";
import { QualityDashboard } from "@/features/dashboards/QualityDashboard";
import { AdminOverviewDashboard } from "@/features/dashboards/AdminOverviewDashboard";
import { OperationalAlertsSection } from "@/features/dashboards/operational-alerts/OperationalAlertsSection";
import { DashboardSwitcher } from "@/features/dashboards/DashboardSwitcher";
import { useRoles } from "@/features/rbac/api";

interface DashboardHeaderConfig {
  title: string;
  subtitle: (branchName: string) => string;
}

const DASHBOARD_CONFIG: Record<DashboardVariant, DashboardHeaderConfig> = {
  admin: {
    title: "Admin Overview",
    subtitle: (branch) => `Hospital-wide operations & lifecycle snapshot — ${branch}.`,
  },
  management: {
    title: "Management Overview",
    subtitle: (branch) => `Strategic executive snapshot across assets, maintenance & compliance — ${branch}.`,
  },
  biomed: {
    title: "Biomedical Overview",
    subtitle: (branch) => `Equipment uptime, work orders & maintenance operations — ${branch}.`,
  },
  finance: {
    title: "Finance Overview",
    subtitle: (branch) => `Asset valuation, depreciation & financial analytics — ${branch}.`,
  },
  department: {
    title: "Department Overview",
    subtitle: (branch) => `Departmental assets, service requests & operational status — ${branch}.`,
  },
  purchase: {
    title: "Purchase & Stores Overview",
    subtitle: (branch) => `Procurement pipeline, store inventory & indent fulfillment — ${branch}.`,
  },
  quality: {
    title: "Quality & Compliance Overview",
    subtitle: (branch) => `Calibration, safety compliance, licences & audit records — ${branch}.`,
  },
};

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: Dashboard,
});

function Dashboard() {
  const { data } = useCurrentUser();
  const { currentBranch } = useBranch();
  const { data: dynamicRoles = [] } = useRoles({ includeInactive: false });
  const variants = useMemo<DashboardVariant[]>(() => (data ? dashboardVariantsForRoles(data.roles) : []), [data]);
  const [variant, setVariant] = useState<DashboardVariant | null>(null);
  if (!data) return null;

  const active = variant ?? defaultDashboardVariant(data.roles);

  // Resolve role label: try built-in first, then dynamic custom roles
  const _getRoleLabel = (r: string) => {
    if (ROLE_LABELS[r as keyof typeof ROLE_LABELS]) return ROLE_LABELS[r as keyof typeof ROLE_LABELS];
    const dr = dynamicRoles.find((d) => d.role_key === r);
    return dr ? dr.role_name : r;
  }; void _getRoleLabel;

  const userName = data.profile?.full_name || data.user.email?.split("@")[0] || "User";
  const branchName = currentBranch?.name ?? "Main Hospital";
  const currentHeader = DASHBOARD_CONFIG[active] ?? DASHBOARD_CONFIG.department;

  return (
    <div className="space-y-0">

      {/* ── Hero Header ──────────────────────────────────────── */}
      <div className="relative mb-0 flex items-center justify-between overflow-hidden rounded-xl bg-gradient-to-r from-slate-800 to-slate-700 dark:from-slate-900 dark:to-slate-800 px-6 py-5">
        {/* Left: welcome + title */}
        <div className="z-10">
          <p className="text-sm text-slate-300">
            Welcome back, <span className="font-medium text-white">{userName}</span>
            {currentBranch ? <> · <span className="text-slate-200">{branchName}</span></> : ""}
          </p>
          <h1 className="mt-1 text-3xl font-bold text-white tracking-tight">{currentHeader.title}</h1>
          <p className="mt-1 text-sm text-slate-300">
            {currentHeader.subtitle(branchName)}
          </p>
        </div>

        {/* Right: tagline */}
        <div className="z-10 hidden md:flex flex-col items-end gap-1 text-right">
          <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-sm">
            <p className="text-sm font-medium italic text-slate-100">"Reliable Assets</p>
            <p className="text-sm font-medium italic text-slate-100">for Healthier</p>
            <p className="text-sm font-semibold text-emerald-400">Tomorrows"</p>
            <div className="mt-1 h-0.5 w-full rounded-full bg-emerald-400/50" />
          </div>
        </div>

        {/* Decorative circle */}
        <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/5" />
        <div className="pointer-events-none absolute -right-8 -bottom-20 h-48 w-48 rounded-full bg-white/5" />
      </div>

      {/* ── Dashboard Tab Switcher ────────────────────────────── */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-1">
        <DashboardSwitcher options={variants} value={active} onChange={setVariant} />
      </div>

      {/* ── Content ──────────────────────────────────────────── */}
      <div className="mt-4 space-y-4">
        {active === "admin" && <AdminOverviewDashboard />}
        {active === "management" && <ManagementDashboard />}
        {active === "biomed" && <BiomedDashboard />}
        {active === "finance" && <FinanceDashboardView />}
        {active === "department" && <DepartmentDashboard />}
        {active === "purchase" && <PurchaseDashboard />}
        {active === "quality" && <QualityDashboard />}

        <OperationalAlertsSection />
      </div>
    </div>
  );
}
